import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, Calendar, TrendingUp, Users, Activity, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colors for charts
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const BetaAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [eventDistribution, setEventDistribution] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsResponse = await axios.get(
        `${API}/admin/beta/analytics/stats?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { withCredentials: true }
      );
      setStats(statsResponse.data);

      // Fetch daily chart data
      const dailyResponse = await axios.get(
        `${API}/admin/beta/analytics/chart-data?chart_type=daily&start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { withCredentials: true }
      );
      setDailyData(dailyResponse.data.data);

      // Fetch weekly chart data
      const weeklyResponse = await axios.get(
        `${API}/admin/beta/analytics/chart-data?chart_type=weekly&start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { withCredentials: true }
      );
      setWeeklyData(weeklyResponse.data.data);

      // Fetch hourly chart data
      const hourlyResponse = await axios.get(
        `${API}/admin/beta/analytics/chart-data?chart_type=hourly&start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { withCredentials: true }
      );
      setHourlyData(hourlyResponse.data.data);

      // Fetch event distribution
      const distributionResponse = await axios.get(
        `${API}/admin/beta/analytics/chart-data?chart_type=event_distribution&start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { withCredentials: true }
      );
      setEventDistribution(distributionResponse.data.data);

    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user, dateRange]);

  // Export data
  const handleExport = async (format) => {
    try {
      const url = `${API}/admin/beta/analytics/export?format=${format}&start_date=${dateRange.start}&end_date=${dateRange.end}`;
      
      // Open in new tab to trigger download
      window.open(url, '_blank');
      
      toast.success(`Export ${format.toUpperCase()} lancé`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Accès réservé aux administrateurs
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Analytics & Observabilité</h1>
          <p className="text-muted-foreground mt-2">
            Mesure de l'utilisation NEXORA AI pendant la Closed Beta
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('json')}>
            <Download className="h-4 w-4 mr-2" />
            JSON
          </Button>
          <Button onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Période d'analyse
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Date de début</Label>
              <Input
                id="start-date"
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">Date de fin</Label>
              <Input
                id="end-date"
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                DAU
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.dau}</p>
              <p className="text-xs text-muted-foreground">Utilisateurs actifs aujourd'hui</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                WAU
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.wau}</p>
              <p className="text-xs text-muted-foreground">Utilisateurs actifs 7 jours</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4 text-green-500" />
                Nouveaux
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{stats.new_users}</p>
              <p className="text-xs text-muted-foreground">Comptes créés</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Événements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.total_events}</p>
              <p className="text-xs text-muted-foreground">Total sur la période</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Activity className="h-4 w-4 text-purple-500" />
                Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-purple-600">{stats.total_sessions}</p>
              <p className="text-xs text-muted-foreground">Sessions uniques</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Durée moy.
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.avg_session_duration_minutes} min</p>
              <p className="text-xs text-muted-foreground">Par session</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Evolution */}
        <Card>
          <CardHeader>
            <CardTitle>Évolution quotidienne</CardTitle>
            <CardDescription>Nombre d'événements par jour</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Weekly Evolution */}
        <Card>
          <CardHeader>
            <CardTitle>Évolution hebdomadaire</CardTitle>
            <CardDescription>Nombre d'événements par semaine</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Hourly Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Activité horaire</CardTitle>
            <CardDescription>Répartition des événements par heure (0-23)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Event Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Répartition des événements</CardTitle>
            <CardDescription>Distribution par type d'événement</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={eventDistribution}
                  dataKey="count"
                  nameKey="event_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {eventDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Event Breakdown Table */}
      {stats && stats.event_breakdown && (
        <Card>
          <CardHeader>
            <CardTitle>Détail des événements</CardTitle>
            <CardDescription>Nombre d'événements par type sur la période</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.event_breakdown).map(([type, count]) => (
                <div key={type} className="p-3 border rounded-lg">
                  <p className="text-sm font-medium text-muted-foreground">{type}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Users */}
      {stats && stats.top_users && stats.top_users.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top 5 utilisateurs actifs</CardTitle>
            <CardDescription>Utilisateurs avec le plus d'événements</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_users.map((u, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 border rounded-lg">
                  <span className="font-medium">{u.email}</span>
                  <span className="text-sm text-muted-foreground">{u.events} événements</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BetaAnalytics;
